import { CurbsuiteTransactionLogEntry } from "common/graphql/queries/GetCurbsuiteTransactions";

/** The Type label an adjustment (chargeback/credit, AUT-6163) row carries, and its filter value. */
export const ADJUSTMENT_TYPE_LABEL = "Adjustment";

export function isAdjustment(row: Pick<CurbsuiteTransactionLogEntry, "transactionType">): boolean {
  return row.transactionType === "adjustment";
}

/**
 * The signed change an adjustment makes **to the charge it references**, in cents: negative for a
 * reversal (chargeback), positive for a credit that adds to the charge.
 *
 * The view's `price` is the adjustment's payer-wallet effect, read off its journals like every other
 * row (AUT-6160). Relative to the referenced charge that sign flips for parks only:
 *
 * - `park` reference: a park debits the wallet, so reversing one *refunds* the wallet (price > 0)
 *   -- the change to the charge is `-price`.
 * - `permit` / `wallet_reload` reference: both credit the wallet's ledger leg, and their
 *   adjustments mirror those journals, so reversing one lowers the wallet (price < 0) -- the change
 *   to the charge is `+price`.
 *
 * This is the one place the frontend derives it; there is no amount column (AUT-6158).
 */
export function getAdjustmentReferenceAmount(
  row: Pick<CurbsuiteTransactionLogEntry, "price" | "referenceTransactionType">,
): number {
  const price = Number(row.price) || 0;
  if (price === 0) {
    return 0;
  }
  return row.referenceTransactionType === "park" ? -price : price;
}

/** An adjustment's recorded reason, trimmed, or null when none was recorded. */
export function getAdjustmentDescription(row: Pick<CurbsuiteTransactionLogEntry, "transaction">): string | null {
  return row.transaction?.description?.trim() || null;
}

/** The referenced charge's ID as the table shows it (zero-padded, like the ID column), or null. */
export function formatReferenceTransactionIndex(
  row: Pick<CurbsuiteTransactionLogEntry, "referenceTransactionIndex">,
): string | null {
  return row.referenceTransactionIndex ? row.referenceTransactionIndex.toString().padStart(6, "0") : null;
}
